import type { JSX } from 'react';
import { memo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Box } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { gridPageCountSelector, gridPaginationModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PaginationMode } from 'common/constants/paginationMode';

import { selectPaginationMode, setPaginationModel } from './palmDataGridSlice';
import { RowCountText } from './RowCountText';

type CustomPaginationProps = {
    rowCount: number;
    isFilterApplied?: boolean;
};

const RowCountCounterweight = () => <Box sx={{ flex: 1 }} />;

const CustomPagination = memo(({ rowCount, isFilterApplied = false }: Readonly<CustomPaginationProps>): JSX.Element => {
    const apiRef = useGridApiContext();
    const dispatch = useAppDispatch();
    const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Client;
    const location = useLocation();

    const resetPaginationToFirstPage = useCallback(() => {
        if (paginationMode === PaginationMode.Client) {
            if (apiRef.current) {
                apiRef.current.setPage(0);
            }
        } else {
            dispatch(setPaginationModel({
                page: 0,
                pageSize: paginationModel.pageSize
            }));
        }
    }, [paginationMode, apiRef, dispatch, paginationModel.pageSize]);

    useEffect(() => {
        resetPaginationToFirstPage();
    }, [location.pathname, location.search, location.hash, resetPaginationToFirstPage]);

    useEffect(() => {
        if (pageCount > 0 && paginationModel.page >= pageCount) {
            resetPaginationToFirstPage();
        }
    }, [pageCount, paginationModel.page, resetPaginationToFirstPage]);

    const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
        apiRef.current.setPage(value - 1);
    }, [apiRef]);

    /*
        A range only earns its place once there is more than one page; with everything on screen
        the count already says it. `pageSize` is the grid's own, so this follows a page-size change.
    */
    const { page, pageSize } = paginationModel;
    const visibleRange = pageCount > 1 && rowCount
        ? { from: page * pageSize + 1, to: Math.min(rowCount, (page + 1) * pageSize) }
        : undefined;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <RowCountText
                count={rowCount}
                isFilterApplied={isFilterApplied}
                visibleRange={visibleRange}
            />
            <Pagination
                sx={{
                    justifyContent: 'center',
                    margin: 'auto',
                    '& .Mui-selected': {
                        backgroundColor: (theme) => `${theme.palette.primary.accent} !important`,
                        color: 'common.white',
                    },
                }}
                color="primary"
                count={pageCount}
                page={paginationModel.page + 1}
                onChange={handlePageChange}
                showFirstButton={true}
                showLastButton={true}
            />
            <RowCountCounterweight />
        </Box>
    );
});

CustomPagination.displayName = 'CustomPagination';

export { CustomPagination };
